<template>
	<v-simple-table dense class="visit-material-rows-table">
		<template v-slot:default>
			<thead class="primary white--text">
				<tr>
					<th v-if="columns.includes('material')" class="white--text">{{ $T('inputs.material') }}</th>
					<th
						v-if="columns.includes('quantity')"
						class="white--text visit-material-rows-table__quantity-cell"
					>
						{{ $T('Visits.quantity-packages') }}
					</th>
					<th v-if="columns.includes('price')" class="white--text">{{ $T('inputs.price') }} ({{ $T('currency') }})</th>
					<th v-if="columns.includes('reason')" class="white--text">{{ $T('inputs.reason') }}</th>
					<th v-if="columns.includes('total')" class="white--text">{{ $T('Visits.total') }} ({{ $T('currency') }})</th>
					<th></th>
				</tr>
			</thead>
			<tbody>
				<tr
					v-for="row in value"
					:key="row.id"
					:class="{ 'visit-material-rows-table__row--empty': isRowEmpty(row) }"
				>
					<td v-if="columns.includes('material')">
						<v-autocomplete
							:value="row.data.materialId"
							:items="materials"
							:item-text="materialLabel"
							item-value="id"
							class="visit-material-rows-table__material-select"
							flat
							solo
							background-color="transparent"
							hide-details="auto"
							:disabled="disabled"
							:item-disabled="materialDisabled"
							:placeholder="$T('inputs.material')"
							:rules="typeof getMaterialRules === 'function' ? getMaterialRules(row) : []"
							@change="val => onMaterialSelect(row, val)"
						>
							<template v-slot:selection="{ item }">
								<span class="visit-material-rows-table__material-selection">{{ materialSelectionLabel(item) }}</span>
							</template>
						</v-autocomplete>
					</td>

					<td v-if="columns.includes('quantity')" class="visit-material-rows-table__quantity-cell">
						<v-text-field
							:value="row.data.amount"
							type="number"
							min="0"
							dense
							flat
							solo
							background-color="transparent"
							hide-details="auto"
							hide-spin-buttons
							:disabled="disabled"
							:rules="typeof getAmountRules === 'function' ? getAmountRules(row) : []"
							placeholder="-"
							@keydown="blockDecimalKey"
							@input="val => onAmountInput(row, val)"
						/>
					</td>

					<td v-if="columns.includes('price')">
						{{ row.data.price ? $options.filters.formatNumber(row.data.price) : '-' }}
					</td>

					<td v-if="columns.includes('reason')">
						<v-select
							:value="row.data.reason"
							:items="reasons"
							item-text="name"
							item-value="id"
							dense
							flat
							solo
							background-color="transparent"
							hide-details
							:disabled="disabled"
							placeholder="-"
							@change="val => onReasonSelect(row, val)"
						/>
					</td>

					<td v-if="columns.includes('total')" class="green--text font-weight-medium">
						{{ row.data.materialId ? $options.filters.formatNumber(rowTotal(row)) : '-' }}
					</td>

					<td class="text-center">
						<v-btn v-if="!isRowEmpty(row) && !disabled" icon small color="error" @click="removeRow(row.id)">
							<v-icon size="18">mdi-close</v-icon>
						</v-btn>
					</td>
				</tr>
			</tbody>
		</template>
	</v-simple-table>
</template>

<script>
import { parseFormattedNumber } from '@/helpers/functions'

export default {
	name: 'VisitMaterialRowsTable',

	model: {
		prop: 'value',
		event: 'change'
	},

	props: {
		value: {
			type: Array,
			required: true
		},
		columns: {
			type: Array,
			required: true
		},
		materials: {
			type: Array,
			required: true
		},
		reasons: {
			type: Array,
			default: () => []
		},
		disabled: {
			type: Boolean,
			default: false
		},
		allowDecimal: {
			type: Boolean,
			default: true
		},
		getMaterialPrice: {
			type: Function,
			default: null
		},
		getFullMaterialName: {
			type: Function,
			default: null
		},
		getMaterialSelectionName: {
			type: Function,
			default: null
		},
		getAmountRules: {
			type: Function,
			default: null
		},
		getMaterialRules: {
			type: Function,
			default: null
		},
		getMaterialDisabled: {
			type: Function,
			default: null
		}
	},

	watch: {
		// the parent can reset the rows asynchronously (draft hydration), which would
		// otherwise wipe the empty row this component seeds on creation
		value: {
			immediate: true,
			handler(rows) {
				if (!rows.length) this.$emit('change', [this.buildEmptyRow(1)])
			}
		}
	},

	methods: {
		materialLabel(material) {
			return typeof this.getFullMaterialName === 'function'
				? this.getFullMaterialName(material)
				: material.name
		},

		materialSelectionLabel(material) {
			return typeof this.getMaterialSelectionName === 'function'
				? this.getMaterialSelectionName(material)
				: this.materialLabel(material)
		},

		materialDisabled(material) {
			return typeof this.getMaterialDisabled === 'function' ? this.getMaterialDisabled(material) : false
		},

		rowTotal(row) {
			return (Number(row.data.amount) || 0) * (Number(row.data.price) || 0)
		},

		isRowEmpty(row) {
			return !row.data.materialId && !row.data.amount
		},

		buildEmptyRow(id) {
			const data = { materialId: null, amount: null }
			if (this.columns.includes('price')) data.price = null
			if (this.columns.includes('reason')) data.reason = null
			return { id, data }
		},

		nextRowId(rows) {
			return rows.length ? Math.max(...rows.map(row => row.id)) + 1 : 1
		},

		withTrailingEmptyRow(rows) {
			if (!rows.length || !this.isRowEmpty(rows[rows.length - 1])) {
				return [...rows, this.buildEmptyRow(this.nextRowId(rows))]
			}
			return rows
		},

		emitRows(rows) {
			this.$emit('change', this.withTrailingEmptyRow(rows))
		},

		updateRow(id, patch) {
			const rows = this.value.map(row =>
				row.id === id ? { ...row, data: { ...row.data, ...patch } } : row
			)
			this.emitRows(rows)
		},

		onMaterialSelect(row, materialId) {
			const patch = { materialId }
			if (this.columns.includes('price') && typeof this.getMaterialPrice === 'function') {
				patch.price = this.getMaterialPrice(materialId)
			}
			this.updateRow(row.id, patch)
		},

		blockDecimalKey(event) {
			if (!this.allowDecimal && (event.key === '.' || event.key === ',')) {
				event.preventDefault()
			}
		},

		onAmountInput(row, val) {
			this.updateRow(row.id, { amount: val === '' ? null : parseFormattedNumber(val, { allowDecimal: this.allowDecimal }) })
		},

		onReasonSelect(row, reason) {
			this.updateRow(row.id, { reason })
		},

		removeRow(id) {
			this.emitRows(this.value.filter(row => row.id !== id))
		}
	}
}
</script>

<style scoped>
/*
 * vuetify reaches these cells through a long structural chain
 * (.v-data-table > .v-data-table__wrapper > table > thead > tr > th), so the
 * overrides below repeat it to out-weigh it rather than reach for !important
 */
.visit-material-rows-table.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th {
	background-color: transparent;
	border-bottom: none;
	padding: 0 4px;
	white-space: nowrap;
}

/* divider between the header cells */
.visit-material-rows-table.v-data-table > .v-data-table__wrapper > table > thead > tr > th:not(:last-child) {
	border-right: 1px solid rgba(255, 255, 255, 0.35);
}

/* full grid: vuetify only draws the horizontal row borders by default */
.visit-material-rows-table.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
	border-right: 1px solid #e0e0e0;
	border-bottom: 1px solid #e0e0e0;
	padding: 0 4px;
}

/* action column: no divider, hugs its button */
.visit-material-rows-table.v-data-table > .v-data-table__wrapper > table > thead > tr > th:last-child,
.visit-material-rows-table.v-data-table > .v-data-table__wrapper > table > tbody > tr > td:last-child {
	width: 1%;
	padding: 0;
	border-right: none;
}

/* quantity column: a packages count never needs more room than this */
.visit-material-rows-table__quantity-cell {
	width: 90px;
	max-width: 90px;
}

/* solo inputs carry their own 12px gutter on top of the cell padding */
.visit-material-rows-table tbody td >>> .v-text-field > .v-input__control > .v-input__slot {
	padding: 0 2px;
}

/* material autocomplete: wrap the selected item instead of truncating it with an ellipsis */
.visit-material-rows-table__material-select >>> .v-select__slot {
	align-items: flex-start;
}

.visit-material-rows-table__material-select >>> .v-select__selection {
	max-width: 100%;
	white-space: normal;
	text-overflow: unset;
	overflow: visible;
	word-break: break-word;
}

.visit-material-rows-table__row--empty {
	background-color: #f5f5f5;
}
</style>
